import { refreshTokenWithDeviceInfo } from './utils/refreshTokenUtils';
import { useCallback, useEffect, useMemo } from 'react';
import { createGlobalState } from 'react-hooks-global-state';
import moment from 'moment';
import useConfigContext from './useConfigContext';
import useDeviceId from './useDeviceId';
import useStorageContext from './useStorageContext';
const KEY_NAME = 'auth-tokens';
const { useGlobalState } = createGlobalState({
    tokens: null
});
async function getRegisterEndpointUrl(identityProviderUrl) {
    const response = await fetch(identityProviderUrl);
    if (response.status !== 200) {
        throw new Error(response.statusText);
    }
    return `${identityProviderUrl}/api/identity/register/`;
}
async function getTokenEndpointUrl(identityProviderUrl) {
    const discoveryUrl = `${identityProviderUrl}/.well-known/openid-configuration`;
    const response = await fetch(discoveryUrl);
    const data = await response.json();
    return data.token_endpoint;
}
export default function useAuth() {
    const storage = useStorageContext();
    const deviceId = useDeviceId();
    const { apiUrl, clientId, clientSecret, deviceName, deviceType, deviceVersion, identityProviderUrl, persistTokens } = useConfigContext();
    const [tokens, setTokens] = useGlobalState('tokens');
    useEffect(() => {
        if (!persistTokens) {
            return;
        }
        if (tokens !== null) {
            const str = JSON.stringify(tokens);
            storage.setItem(KEY_NAME, str);
            return;
        }
        const str = storage.getItem(KEY_NAME);
        if (str) {
            const tks = JSON.parse(str);
            // Convert expiration strings back to Date objects
            const tokensWithDates = tks.map((token) => ({
                ...token,
                expiration: new Date(token.expiration)
            }));
            setTokens(tokensWithDates);
        }
        else {
            setTokens([]);
        }
    }, [persistTokens, setTokens, storage, tokens]);
    const clearAccessToken = useCallback((email) => {
        if (!tokens) {
            return;
        }
        const index = tokens.findIndex((token) => token.email === email);
        if (index >= 0) {
            const updated = tokens.slice(0);
            updated.splice(index, 1);
            setTokens(updated);
        }
    }, [tokens, setTokens]);
    const forgotPassword = useCallback(async (email) => {
        const endpointUrl = `${identityProviderUrl}/api/identity/forgotpassword/`;
        const body = JSON.stringify({ Email: email });
        const response = await fetch(endpointUrl, {
            method: 'POST',
            body,
            headers: { 'Content-Type': 'application/json' }
        });
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
        return response.json();
    }, [identityProviderUrl]);
    const getAccessToken = useCallback((email) => {
        if (!tokens) {
            return null;
        }
        const index = tokens.findIndex((token) => token.email === email);
        if (index >= 0) {
            const token = tokens[index];
            if (moment().isAfter(token.expiration)) {
                // Keep token (with refresh token) so useTokenRenewal can attempt renewal.
                // Only clear when we have no refresh token - can't renew without it.
                if (token.refreshToken) {
                    return token;
                }
                const updated = tokens.slice(0);
                updated.splice(index, 1);
                setTokens(updated);
                return null;
            }
            return token;
        }
        return null;
    }, [tokens, setTokens]);
    const login = useCallback(async (email, password) => {
        var _a;
        // Check if required device parameters are configured for traditional login
        if (!deviceId || !deviceName || !deviceType || !deviceVersion) {
            throw new Error('Device parameters not configured. Please use OIDC authentication instead.');
        }
        const existing = getAccessToken(email);
        if (existing) {
            return existing;
        }
        const endpointUrl = await getTokenEndpointUrl(identityProviderUrl);
        const now = moment();
        const body = new FormData();
        body.append('deviceid', deviceId);
        body.append('devicename', deviceName);
        body.append('devicetype', deviceType);
        body.append('deviceversion', deviceVersion);
        body.append('grant_type', 'password');
        body.append('username', email);
        body.append('password', password);
        body.append('scope', 'vengarapi');
        body.append('client_id', clientId);
        body.append('client_secret', clientSecret);
        const response = await fetch(endpointUrl, { method: 'POST', body });
        if (response.status !== 200) {
            if ((_a = response.headers.get('Content-Type')) === null || _a === void 0 ? void 0 : _a.includes('application/json')) {
                const json = await response.json();
                if (json.error_description) {
                    throw new Error(json.error_description);
                }
                if (json.error) {
                    throw new Error(json.error);
                }
                throw new Error(json.error_description);
            }
            throw new Error(response.statusText);
        }
        const data = await response.json();
        const token = {
            accessToken: data.access_token,
            email,
            expiration: now.add(data.expires_in, 'seconds').toDate()
        };
        const updated = (tokens || []).concat([token]);
        setTokens(updated);
        return token;
    }, [
        clientId,
        clientSecret,
        deviceId,
        deviceName,
        deviceType,
        deviceVersion,
        getAccessToken,
        identityProviderUrl,
        setTokens,
        tokens
    ]);
    const register = useCallback(async (email, password) => {
        const endpointUrl = await getRegisterEndpointUrl(identityProviderUrl);
        const body = JSON.stringify({ username: email, password });
        const response = await fetch(endpointUrl, {
            method: 'POST',
            body,
            headers: { 'Content-Type': 'application/json' }
        });
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
    }, [identityProviderUrl]);
    const resendVerificationEmail = useCallback(async (email) => {
        const endpointUrl = `${identityProviderUrl}/api/identity/resendconfirmemail/`;
        const body = JSON.stringify({ Email: email });
        const response = await fetch(endpointUrl, {
            method: 'POST',
            body,
            headers: { 'Content-Type': 'application/json' }
        });
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
        return response.json();
    }, [identityProviderUrl]);
    const deleteAccount = useCallback(async (email) => {
        const token = getAccessToken(email);
        if (!token) {
            throw new Error(`Not logged into account ${email}`);
        }
        const parts = token.accessToken.split('.');
        if (parts.length !== 3) {
            throw new Error('jwt malformed');
        }
        const [, claims] = parts;
        const str = Buffer.from(claims, 'base64').toString('utf8');
        const json = JSON.parse(str);
        if (!('sub' in json)) {
            throw new Error('No subject in jwt');
        }
        const endpointUrl = `${apiUrl}/accounts/${json.sub}`;
        const response = await fetch(endpointUrl, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token.accessToken}`
            }
        });
        if (response.status !== 200) {
            throw new Error(response.statusText);
        }
        return response.json();
    }, [apiUrl, getAccessToken]);
    const setAccessToken = useCallback((email, accessToken, expiration) => {
        const token = {
            accessToken,
            email,
            expiration
        };
        if (!tokens) {
            setTokens([token]);
            return;
        }
        const index = tokens.findIndex((t) => t.email === email);
        if (index >= 0) {
            // Update existing token
            const updated = tokens.slice(0);
            updated[index] = token;
            setTokens(updated);
        }
        else {
            // Add new token
            const updated = tokens.concat([token]);
            setTokens(updated);
        }
    }, [tokens, setTokens]);
    const oidcLogin = useCallback((email, accessToken, expiresAt, refreshToken) => {
        const token = {
            accessToken,
            email,
            expiration: expiresAt
                ? new Date(expiresAt * 1000) // Convert seconds to milliseconds
                : moment().add(30, 'minutes').toDate(),
            refreshToken
        };
        if (!tokens) {
            setTokens([token]);
        }
        else {
            const index = tokens.findIndex((t) => t.email === email);
            if (index >= 0) {
                // Update existing token
                const updated = tokens.slice(0);
                updated[index] = token;
                setTokens(updated);
            }
            else {
                // Add new token
                const updated = tokens.concat([token]);
                setTokens(updated);
            }
        }
        return token;
    }, [tokens, setTokens]);
    const refreshAccessToken = useCallback(async (email) => {
        const token = getAccessToken(email);
        if (!token) {
            throw new Error(`Not logged into account ${email}`);
        }
        // Check if required device parameters are configured
        if (!deviceId || !deviceName || !deviceType || !deviceVersion) {
            throw new Error('Device parameters not configured. Please configure deviceId, deviceName, deviceType, and deviceVersion.');
        }
        const endpointUrl = await getTokenEndpointUrl(identityProviderUrl);
        const deviceInfo = {
            deviceId,
            deviceName,
            deviceType,
            deviceVersion
        };
        const result = await refreshTokenWithDeviceInfo({
            refreshToken: token.refreshToken || '',
            clientId,
            clientSecret,
            tokenEndpoint: endpointUrl,
            deviceInfo
        });
        // Use oidcLogin to properly update both access token and refresh token
        const updatedToken = oidcLogin(email, result.accessToken, result.expiresAt, result.refreshToken);
        return updatedToken;
    }, [
        clientId,
        clientSecret,
        getAccessToken,
        identityProviderUrl,
        oidcLogin,
        deviceId,
        deviceName,
        deviceType,
        deviceVersion
    ]);
    return useMemo(() => ({
        clearAccessToken,
        deleteAccount,
        forgotPassword,
        getAccessToken,
        login,
        oidcLogin,
        refreshAccessToken,
        register,
        resendVerificationEmail,
        setAccessToken
    }), [
        clearAccessToken,
        deleteAccount,
        forgotPassword,
        getAccessToken,
        login,
        oidcLogin,
        refreshAccessToken,
        register,
        resendVerificationEmail,
        setAccessToken
    ]);
}
